// =====================================================================
// SoldesParUtilisateur.jsx — Tab Congés (Day 9)
// =====================================================================
// Tabel compact 1 rând per user, coloane pe tipuri (VA, VAN, ...).
// Click pe rând → modal cu istoricul complet al userului + soldes detaliate.
// Permission gate: leaves.read.all
// =====================================================================

import { useState, useEffect, useMemo } from 'react';
import { leaveBalances as balancesApi, leaves as leavesApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';   // Day 11 — bouton Annuler congé
import { comparerPersonnes } from '../../lib/tri-personnes.js';
import { celluleCsv } from '../../lib/csv.js';

const LEAVE_TYPE_LABELS = {
  VA:     'Vacances annuelles',
  VAN:    'Vacances annuelles N+1',
  VAEN1:  'Vacances annuelles N-1',
  AA:     'Absences autorisées',
  REC:    'Récupération',
  MAT:    'Maternité',
  PAT:    'Paternité',
  MAL:    'Maladie',
  'VA-0.5': 'Demi-journée',
};

// Tipuri afișate ca coloane (cu quota — restul "MAL" + "VA-0.5" se văd doar în modal)
const COLUMN_TYPES = ['VA', 'VAN', 'VAEN1', 'AA', 'REC', 'MAT', 'PAT'];

const STATUS_LABELS = {
  PENDING:   'En attente',
  APPROVED:  'Approuvée',
  REJECTED:  'Refusée',
  CANCELLED: 'Annulée',
};

const STATUS_PILL_STYLES = {
  PENDING:   { background: '#FEF3C7', color: '#78350F', border: '1px solid #F59E0B' },
  APPROVED:  { background: '#DCFCE7', color: '#166534', border: '1px solid #86EFAC' },
  REJECTED:  { background: '#FEE2E2', color: '#7F1D1D', border: '1px solid #FCA5A5' },
  CANCELLED: { background: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E1' },
};

function pillBaseStyle() {
  return { display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' };
}

function statusPill(status) {
  const s = STATUS_PILL_STYLES[status] || STATUS_PILL_STYLES.CANCELLED;
  return { ...pillBaseStyle(), ...s };
}

function formatDateFR(ymd) {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

export default function SoldesParUtilisateur() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all | with-pending | with-rejected
  const [sortBy, setSortBy] = useState('name'); // name | va-remaining | pending-desc

  // Drilldown modal
  const [drillUser, setDrillUser] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const list = await balancesApi.listAllUsers(year, includeInactive);
      setRows(list);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [year, includeInactive]);

  const filteredSortedRows = useMemo(() => {
    let list = [...rows];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) =>
        r.userName?.toLowerCase().includes(q) ||
        r.username?.toLowerCase().includes(q) ||
        r.groupName?.toLowerCase().includes(q) ||
        r.role?.toLowerCase().includes(q)
      );
    }

    if (statusFilter === 'with-pending') {
      list = list.filter((r) => (r.statusCounts?.PENDING || 0) > 0);
    } else if (statusFilter === 'with-rejected') {
      list = list.filter((r) => (r.statusCounts?.REJECTED || 0) > 0);
    } else if (statusFilter === 'no-activity') {
      list = list.filter((r) =>
        !r.statusCounts ||
        ((r.statusCounts.PENDING || 0) + (r.statusCounts.APPROVED || 0) + (r.statusCounts.REJECTED || 0) + (r.statusCounts.CANCELLED || 0)) === 0
      );
    }

    list.sort((a, b) => {
      if (sortBy === 'name') return comparerPersonnes(a, b);
      if (sortBy === 'va-remaining') {
        const ra = a.quotas?.find((q) => q.type === 'VA')?.remaining || 0;
        const rb = b.quotas?.find((q) => q.type === 'VA')?.remaining || 0;
        return rb - ra;
      }
      if (sortBy === 'pending-desc') {
        return (b.statusCounts?.PENDING || 0) - (a.statusCounts?.PENDING || 0);
      }
      return 0;
    });

    return list;
  }, [rows, search, statusFilter, sortBy]);

  function exportCSV() {
    const headers = ['Opérateur', 'Username', 'Rôle', 'Groupe', ...COLUMN_TYPES.flatMap((t) => [t + ' restant', t + ' total', t + ' utilisés', t + ' en attente']), 'P', 'A', 'R', 'C'];
    const lines = [headers.join(',')];
    filteredSortedRows.forEach((r) => {
      const cells = [r.userName, r.username, r.role, r.groupName || ''];
      for (const t of COLUMN_TYPES) {
        const q = r.quotas?.find((x) => x.type === t) || { remaining: 0, totalAllocated: 0, used: 0, pending: 0 };
        cells.push(q.remaining, q.totalAllocated, q.used, q.pending);
      }
      cells.push(r.statusCounts?.PENDING || 0, r.statusCounts?.APPROVED || 0, r.statusCounts?.REJECTED || 0, r.statusCounts?.CANCELLED || 0);
      lines.push(cells.map(celluleCsv).join(','));   // formule (= + - @) neutralisée : lib/csv.js
    });
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `soldes-conges-${year}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>Chargement...</div>;
  }
  if (error) {
    return (
      <div style={{ padding: 24, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b' }}>
        <strong>Erreur :</strong> {error}
        <button onClick={load} style={{ marginLeft: 12, padding: '4px 10px' }}>Réessayer</button>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
        <input
          type="text"
          placeholder="🔍 Rechercher (nom, username, groupe, rôle)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, minWidth: 240, flex: 1, maxWidth: 320 }}
        />
        <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} style={selectStyle}>
          {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
            <option key={y} value={y}>Année {y}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="all">Tous les opérateurs</option>
          <option value="with-pending">Avec demande en attente</option>
          <option value="with-rejected">Avec demande refusée</option>
          <option value="no-activity">Sans activité</option>
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={selectStyle}>
          <option value="name">Tri : Nom</option>
          <option value="va-remaining">Tri : VA restant ↓</option>
          <option value="pending-desc">Tri : Pending ↓</option>
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          Inclure inactifs
        </label>
        <div style={{ flex: '1 0 auto' }} />
        <button onClick={exportCSV} style={btnStyle} title="Export CSV avec filtres appliqués">📥 CSV</button>
      </div>

      <div style={{ marginBottom: 8, fontSize: 13, color: '#475569', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <span><strong>{filteredSortedRows.length}</strong> opérateur(s) · Année <strong>{year}</strong> · Cliquer sur un opérateur pour voir le détail.</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', fontSize: 11, color: '#64748b' }}>
          <span style={{ marginRight: 2 }}>Légende :</span>
          <span style={statusPill('PENDING')}>P</span><span>= En attente</span>
          <span style={{ ...statusPill('APPROVED'), marginLeft: 8 }}>A</span><span>= Approuvée</span>
          <span style={{ ...statusPill('REJECTED'), marginLeft: 8 }}>R</span><span>= Refusée</span>
          <span style={{ ...statusPill('CANCELLED'), marginLeft: 8 }}>C</span><span>= Annulée</span>
          <span style={{ marginLeft: 12, color: '#92400e' }}>⏳</span><span>= en attente (jours)</span>
          <span style={{ marginLeft: 8, color: '#64748b' }}>u:</span><span>= utilisés (jours)</span>
        </span>
      </div>

      {filteredSortedRows.length === 0 ? (
        <div style={{ padding: 32, background: '#F0FDF4', border: '1px dashed #86EFAC', borderRadius: 8, textAlign: 'center', color: '#16a34a' }}>
          Aucun opérateur ne correspond aux filtres.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#F1F5F9', borderBottom: '1px solid #CBD5E1' }}>
                <th style={{ padding: 8, textAlign: 'left', position: 'sticky', left: 0, background: '#F1F5F9', zIndex: 1 }}>Opérateur</th>
                {COLUMN_TYPES.map((t) => (
                  <th key={t} style={{ padding: 8, textAlign: 'center', minWidth: 70 }} title={LEAVE_TYPE_LABELS[t]}>{t}</th>
                ))}
                <th style={{ padding: 8, textAlign: 'center', minWidth: 110 }}>Statuts (P/A/R/C)</th>
              </tr>
            </thead>
            <tbody>
              {filteredSortedRows.map((r) => (
                <tr
                  key={r.userId}
                  onClick={() => setDrillUser(r)}
                  style={{ borderBottom: '1px solid #E2E8F0', opacity: r.isActive === false ? 0.55 : 1, cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  title="Cliquer pour voir le détail"
                >
                  <td style={{ padding: 8, position: 'sticky', left: 0, background: 'inherit' }}>
                    <div style={{ fontWeight: 500 }}>
                      {r.userName}
                      {r.isActive === false && <span style={{ marginLeft: 6, fontSize: 10, color: '#94a3b8' }}>(inactif)</span>}
                    </div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.username} · {r.role} · {r.groupName || '—'}</div>
                  </td>
                  {COLUMN_TYPES.map((t) => {
                    const q = r.quotas?.find((x) => x.type === t) || { remaining: 0, totalAllocated: 0, used: 0, pending: 0 };
                    const hasPending = q.pending > 0;
                    const isUsed = q.used > 0;
                    const isEmpty = q.totalAllocated === 0 && q.used === 0 && q.pending === 0;
                    return (
                      <td key={t} style={{ padding: 6, textAlign: 'center' }}>
                        {isEmpty ? (
                          <span style={{ color: '#cbd5e1' }}>—</span>
                        ) : (
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 12, color: q.remaining > 0 ? '#15803d' : (q.remaining < 0 ? '#dc2626' : '#64748b') }}>
                              {q.remaining}
                              <span style={{ color: '#94a3b8', fontWeight: 400 }}>/{q.totalAllocated}</span>
                            </div>
                            {(isUsed || hasPending) && (
                              <div style={{ fontSize: 9, lineHeight: 1.3, color: '#64748b' }}>
                                {isUsed && <span>u:{q.used}</span>}
                                {isUsed && hasPending && <span> · </span>}
                                {hasPending && <span style={{ color: '#92400e', fontWeight: 600 }}>⏳{q.pending}</span>}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ padding: 6, textAlign: 'center', fontSize: 11 }}>
                    <span title="Pending" style={{ ...statusPill('PENDING'), opacity: (r.statusCounts?.PENDING || 0) > 0 ? 1 : 0.3, marginRight: 2 }}>
                      P:{r.statusCounts?.PENDING || 0}
                    </span>
                    <span title="Approved" style={{ ...statusPill('APPROVED'), opacity: (r.statusCounts?.APPROVED || 0) > 0 ? 1 : 0.3, marginRight: 2 }}>
                      A:{r.statusCounts?.APPROVED || 0}
                    </span>
                    <span title="Rejected" style={{ ...statusPill('REJECTED'), opacity: (r.statusCounts?.REJECTED || 0) > 0 ? 1 : 0.3, marginRight: 2 }}>
                      R:{r.statusCounts?.REJECTED || 0}
                    </span>
                    <span title="Cancelled" style={{ ...statusPill('CANCELLED'), opacity: (r.statusCounts?.CANCELLED || 0) > 0 ? 1 : 0.3 }}>
                      C:{r.statusCounts?.CANCELLED || 0}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drillUser && (
        <UserDetailModal row={drillUser} year={year} onClose={() => setDrillUser(null)} />
      )}
    </div>
  );
}

// =====================================================================
// Sub-component: UserDetailModal
// =====================================================================
// Pulls all leaves for the selected user (via /admin/all?userId=X) and
// renders complete balance + full history.
// =====================================================================
function UserDetailModal({ row, year, onClose }) {
  const { can } = useAuth();
  const canCancel = can('leaves.cancel.approved');
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [busyId, setBusyId] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const list = await leavesApi.listAll({ userId: row.userId, limit: 500 });
      setLeaves(list);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  // Day 11 — annuler un congé APPROUVÉ (cascade) depuis Soldes par utilisateur
  async function handleCancel(leave) {
    if (!window.confirm(`Annuler le congé ${leave.type} du ${formatDateFR(leave.startDate)} au ${formatDateFR(leave.endDate)} ? Supprime Attendance + TS et restitue le solde.`)) return;
    setBusyId(leave.id);
    try { await leavesApi.cancelApproved(leave.id); await load(); }
    catch (e) { window.alert(e?.response?.data?.message || e.message); }
    finally { setBusyId(null); }
  }

  useEffect(() => { load(); }, [row.userId]);

  const filteredLeaves = useMemo(() => {
    if (statusFilter === 'ALL') return leaves;
    return leaves.filter((l) => l.status === statusFilter);
  }, [leaves, statusFilter]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
    }}>
      <div style={{ background: 'white', borderRadius: 8, padding: 20, maxWidth: 920, width: '100%', maxHeight: '92vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18 }}>{row.userName}</h2>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              {row.username} · {row.role} · {row.groupName || '—'} · Année {year}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>

        {/* Soldes detaliate per tip — Day 9d: 5 lignes (Reste / Encodé / Utilisé / À venir / En attente) */}
        <h3 style={{ fontSize: 14, marginBottom: 8, color: '#1e293b' }}>Soldes — {year}</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
          {row.quotas.filter((q) => q.totalAllocated > 0 || q.used > 0 || q.pending > 0).map((q) => {
            const hasPending = q.pending > 0;
            const reste = q.resteAProgrammer ?? (q.totalAllocated - q.used - q.pending);
            const encoded = q.encoded ?? q.used;
            const usedPast = q.usedPast ?? 0;
            const aVenir = q.aVenir ?? Math.max(0, q.used - usedPast);
            const positive = reste > 0;
            return (
              <div key={q.type} style={{
                background: positive ? '#FAEEDA' : '#F1F5F9',
                color: positive ? '#633806' : '#64748B',
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid ' + (positive ? '#E8D4B0' : '#CBD5E1'),
                minWidth: 200,
              }} title={LEAVE_TYPE_LABELS[q.type]}>
                <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{q.type}</div>
                <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.1 }}>
                  {reste}<span style={{ opacity: 0.55, fontWeight: 400, fontSize: 14 }}> / {q.totalAllocated}</span> j
                </div>
                <div style={{ fontSize: 9, opacity: 0.6, marginTop: 1, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  reste à programmer
                </div>
                <div style={{ borderTop: '1px solid ' + (positive ? '#E8D4B0' : '#CBD5E1'), paddingTop: 5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ fontSize: 10.5, display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ opacity: 0.75 }}>Encodé</span>
                    <strong>{encoded} j</strong>
                  </div>
                  <div style={{ fontSize: 10, display: 'flex', justifyContent: 'space-between', opacity: 0.75, paddingLeft: 8 }}>
                    <span>├ Utilisé <span style={{ opacity: 0.6 }}>(passé)</span></span>
                    <span><strong>{usedPast}</strong> j</span>
                  </div>
                  <div style={{ fontSize: 10, display: 'flex', justifyContent: 'space-between', opacity: 0.75, paddingLeft: 8 }}>
                    <span>└ À venir <span style={{ opacity: 0.6 }}>(programmé)</span></span>
                    <span><strong>{aVenir}</strong> j</span>
                  </div>
                  <div style={{ fontSize: 10.5, display: 'flex', justifyContent: 'space-between', color: hasPending ? '#92400e' : 'inherit', fontWeight: hasPending ? 600 : 'normal' }}>
                    <span style={{ opacity: hasPending ? 1 : 0.6 }}>En attente</span>
                    <strong>{q.pending} j</strong>
                  </div>
                </div>
              </div>
            );
          })}
          {row.quotas.every((q) => q.totalAllocated === 0 && q.used === 0 && q.pending === 0) && (
            <div style={{ padding: 12, color: '#94a3b8', fontSize: 13 }}>Aucun quota défini pour cet utilisateur.</div>
          )}
        </div>

        {/* Filtre statut + historique */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h3 style={{ fontSize: 14, margin: 0, color: '#1e293b' }}>Historique des demandes ({leaves.length})</h3>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
            <option value="ALL">Tous les statuts</option>
            <option value="PENDING">En attente ({row.statusCounts?.PENDING || 0})</option>
            <option value="APPROVED">Approuvées ({row.statusCounts?.APPROVED || 0})</option>
            <option value="REJECTED">Refusées ({row.statusCounts?.REJECTED || 0})</option>
            <option value="CANCELLED">Annulées ({row.statusCounts?.CANCELLED || 0})</option>
          </select>
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Chargement...</div>
        ) : error ? (
          <div style={{ padding: 12, background: '#fef2f2', color: '#991b1b', borderRadius: 6 }}>{error}</div>
        ) : filteredLeaves.length === 0 ? (
          <div style={{ padding: 20, background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 6, textAlign: 'center', color: '#64748b' }}>
            Aucune demande pour le filtre sélectionné.
          </div>
        ) : (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#F1F5F9' }}>
                  <th style={{ padding: 8, textAlign: 'left' }}>Type</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>Du</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>Au</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Jours</th>
                  <th style={{ padding: 8, textAlign: 'center' }}>Statut</th>
                  <th style={{ padding: 8, textAlign: 'center' }}>Décidée le</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>Motif / Décision</th>
                  {canCancel && <th style={{ padding: 8, textAlign: 'center' }}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {filteredLeaves.map((l) => (
                  <tr key={l.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: 8, fontWeight: 500 }}>{l.type}</td>
                    <td style={{ padding: 8 }}>{formatDateFR(l.startDate)}</td>
                    <td style={{ padding: 8 }}>{formatDateFR(l.endDate)}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{l.workdaysCount ?? '—'}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      <span style={statusPill(l.status)}>{STATUS_LABELS[l.status] || l.status}</span>
                    </td>
                    <td style={{ padding: 8, textAlign: 'center', color: '#64748b' }}>
                      {l.decidedAt ? formatDateFR(l.decidedAt.slice(0, 10)) : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td style={{ padding: 8, color: '#475569' }}>
                      {l.reason && <div>📝 {l.reason}</div>}
                      {l.decidedReason && <div style={{ color: '#92400e' }}>↪ {l.decidedReason}</div>}
                      {!l.reason && !l.decidedReason && <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    {canCancel && (
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        {l.status === 'APPROVED'
                          ? <button onClick={() => handleCancel(l)} disabled={busyId === l.id} style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid #fca5a5', background: 'white', color: '#991b1b', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{busyId === l.id ? '...' : '✗ Annuler'}</button>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {/* Day 9d — totalul jours ouvrés cu split APPROVED/PENDING/REJECTED+CANCELLED */}
              <tfoot>
                {(() => {
                  const totApproved = filteredLeaves.filter(l => l.status === 'APPROVED').reduce((s, l) => s + (l.workdaysCount || 0), 0);
                  const totPending  = filteredLeaves.filter(l => l.status === 'PENDING').reduce((s, l) => s + (l.workdaysCount || 0), 0);
                  const totOther    = filteredLeaves.filter(l => l.status === 'REJECTED' || l.status === 'CANCELLED').reduce((s, l) => s + (l.workdaysCount || 0), 0);
                  return (
                    <tr style={{ background: '#F8FAFC', borderTop: '2px solid #CBD5E1', fontWeight: 600 }}>
                      <td colSpan={3} style={{ padding: 8, textAlign: 'right', color: '#334155' }}>Total jours ouvrés :</td>
                      <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                        {totApproved + totPending + totOther}
                      </td>
                      <td colSpan={canCancel ? 4 : 3} style={{ padding: 8, fontSize: 11, color: '#64748b', fontWeight: 400 }}>
                        <span style={{ color: '#166534' }}>● Approuvés : <strong>{totApproved}</strong></span>
                        {totPending > 0 && <span style={{ marginLeft: 12, color: '#92400e' }}>● En attente : <strong>{totPending}</strong></span>}
                        {totOther > 0 && <span style={{ marginLeft: 12, color: '#475569' }}>● Refusés/Annulés : <strong>{totOther}</strong></span>}
                      </td>
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer' }}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

const selectStyle = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: 'white' };
const btnStyle    = { padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 13 };
